import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { acquisitionsApi } from "@/lib/api/acquisitions";
import { useToast } from "@/contexts/ToastContext";
import {
  Loader2,
  Send,
  BookOpen,
  CheckCircle2,
  Info,
  LayoutDashboard,
  BookPlus,
  ClipboardList,
} from "lucide-react";

const fieldIds = {
  title: "recommend-title",
  author: "recommend-author",
  isbn: "recommend-isbn",
  urgency: "recommend-urgency",
  justification: "recommend-justification",
} as const;

export default function PatronRecommendPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    title: "",
    author: "",
    isbn: "",
    justification: "",
    urgency: "MEDIUM",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Please enter a title.");
      return;
    }

    setSaving(true);
    try {
      const created = await acquisitionsApi.requests.create({
        title: form.title.trim(),
        author: form.author || undefined,
        isbn: form.isbn || undefined,
        justification: form.justification || undefined,
        urgency: form.urgency,
        quantity: 1,
      });
      await acquisitionsApi.requests.submit(created.id);
      toast.success(
        "Your recommendation was submitted for review. Staff will see it in the purchase request queue.",
        "Submitted"
      );
      setSubmitted(true);
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast.error(
        typeof message === "string" && message.trim()
          ? message
          : "Failed to submit recommendation. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const resetForm = () => {
    setSubmitted(false);
    setForm({
      title: "",
      author: "",
      isbn: "",
      justification: "",
      urgency: "MEDIUM",
    });
  };

  if (submitted) {
    return (
      <PageContainer width="wide">
        <PageHeader
          title="Recommend a title"
          description={
            tenant?.name
              ? `Suggest a book or resource for ${tenant.name} to consider acquiring`
              : "Suggest a book or resource for the library to acquire"
          }
          breadcrumbItems={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Recommend a title" },
          ]}
          showSearch={false}
          titleSize="md"
        />
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-8 pb-6">
            <EmptyState
              icon={<CheckCircle2 className="h-7 w-7 text-emerald-600" aria-hidden />}
              title="Recommendation submitted"
              description={
                tenant?.name
                  ? `Thank you. ${tenant.name} will review your suggestion with other acquisition requests. Track status anytime under My recommendations.`
                  : "Thank you. Our acquisitions team will review your suggestion with other acquisition requests. Track status anytime under My recommendations."
              }
              className="py-8"
              action={
                <div className="flex w-full max-w-md flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
                  <Button variant="outline" className="min-h-11" asChild>
                    <Link to="/my-recommendations">
                      <ClipboardList className="mr-2 h-4 w-4" />
                      My recommendations
                    </Link>
                  </Button>
                  <Button variant="outline" className="min-h-11" asChild>
                    <Link to="/dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  </Button>
                  <Button className="min-h-11" onClick={resetForm}>
                    <BookOpen className="mr-2 h-4 w-4" />
                    Recommend another
                  </Button>
                </div>
              }
            />
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Recommend a title"
        description={
          tenant?.name
            ? `Suggest a book or resource for ${tenant.name} to consider acquiring`
            : "Suggest a book or resource for the library to acquire"
        }
        breadcrumbItems={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Recommend a title" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <Alert variant="info" className="items-start">
        <div className="flex gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-200 bg-white">
            <Info className="h-4 w-4 text-primary" aria-hidden />
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold text-slate-900">How it works</p>
            <AlertDescription>
              Staff review patron recommendations along with other acquisition requests.{" "}
              <Link
                to="/my-recommendations"
                className="font-medium text-primary underline underline-offset-2 hover:no-underline"
              >
                My recommendations
              </Link>{" "}
              lists your submissions and current status. Thank you for helping shape the collection.
            </AlertDescription>
          </div>
        </div>
      </Alert>

      <form onSubmit={handleSubmit} noValidate>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <BookPlus className="h-5 w-5 text-primary" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-heading text-lg text-slate-900">
                  Resource details
                </CardTitle>
                <CardDescription className="mt-1 text-slate-500">
                  Required fields are marked with an asterisk.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-5 pt-6">
            <div className="space-y-2">
              <Label htmlFor={fieldIds.title} required>
                Title
              </Label>
              <Input
                id={fieldIds.title}
                name="title"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="Book or resource title"
                className="border-slate-200 bg-white"
                autoComplete="off"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={fieldIds.author}>Author</Label>
              <Input
                id={fieldIds.author}
                name="author"
                value={form.author}
                onChange={(e) => update("author", e.target.value)}
                placeholder="Author or editor (if known)"
                className="border-slate-200 bg-white"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={fieldIds.isbn}>ISBN</Label>
              <Input
                id={fieldIds.isbn}
                name="isbn"
                value={form.isbn}
                onChange={(e) => update("isbn", e.target.value)}
                placeholder="ISBN-10 or ISBN-13 (optional)"
                className="border-slate-200 bg-white font-mono text-sm"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={fieldIds.urgency}>Urgency</Label>
              <Select value={form.urgency} onValueChange={(v) => update("urgency", v)}>
                <SelectTrigger id={fieldIds.urgency} className="border-slate-200 bg-white">
                  <SelectValue placeholder="Select urgency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low — nice to have</SelectItem>
                  <SelectItem value="MEDIUM">Medium — needed soon</SelectItem>
                  <SelectItem value="HIGH">High — urgently needed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor={fieldIds.justification}>Why is this needed?</Label>
              <Textarea
                id={fieldIds.justification}
                name="justification"
                value={form.justification}
                onChange={(e) => update("justification", e.target.value)}
                rows={4}
                placeholder="Course, research topic, or why this would benefit patrons…"
                className="resize-none border-slate-200 bg-white"
              />
              <p className="text-xs text-slate-500">
                Optional but helpful for selectors reviewing the request.
              </p>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/60 py-5 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="min-h-11 w-full sm:w-auto"
              onClick={() => navigate("/dashboard")}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="min-h-11 w-full sm:w-auto">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit recommendation
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </PageContainer>
  );
}
